/**
 * The locale surface a miniapp has to agree with the rest of the platform on.
 *
 * `SUPPORTED_LOCALES` is the contract enforced by `src/app/lang/translations.spec.ts`: every tag here
 * must have a `lang/<tag>.json` catalog AND a row in the loader's `rawMessagesMap`. Adding a locale
 * means all three, and the spec fails if any one is missed.
 */

/** Language used when the host passes nothing, or a tag with no catalog behind it. */
export const DEFAULT_LANG = 'en';

/**
 * Fallback IANA zone for a building row with a missing or malformed timezone. `UTC` rather than a
 * business timezone on purpose — a wrong-but-plausible zone (`America/New_York`) silently shifts
 * calendar days by a few hours and reads as correct; `UTC` is obviously a fallback when you see it.
 */
export const DEFAULT_TIMEZONE = 'UTC';

/**
 * Every locale this miniapp ships a catalog for.
 *
 * The English set is regional for a reason: `en` is not one region's worth of conventions. `en-US`
 * writes month-first, `en-GB`/`en-IE`/`en-AU` write day-first, `en-CA` is ISO-ish. Same words,
 * different dates — so each region gets its own tag even though the copy is identical.
 *
 * ⚠️ Those five English files are deliberately EMPTY. They exist so the TAG resolves and date/number
 * formatting goes regional; the loader merges them over `en`, so every key still resolves. Nothing in
 * this catalogue has a British/Irish/Australian spelling divergence (no `colour`, no `-ise`), so an
 * override would be dead weight that silently keeps the old wording the next time `en` is edited.
 * If you add copy that DOES diverge, put the override in the relevant files — `translations.spec.ts`
 * fails if you don't, and en-CA takes `-our` but keeps American `-ize`.
 *
 * ⚠️ Bare `en` is the base catalogue and is region-less on purpose — it is what resolves when the host
 * tells us nothing, so its date order and clock should follow the DEVICE.
 *
 * ⚠️ **Polish is bare `pl`, and that is deliberate here even though it is wrong in principle.** The
 * platform's frontends have shipped `pl.json` since hqos added Polish in 2022 (`8413bb82f`, which gave
 * `it-IT` and `nb-NO` regions in the very same commit and left Polish bare). 19 of 20 frontends still
 * do. Tenant portal and the mobile apps therefore send bare `pl`, so this tag, the catalog filename
 * and the `rawMessagesMap` row must all stay `pl` — renaming it here would make this template the
 * outlier and would not help the tag hosts actually send.
 *
 * `@hqo/date-time` keeps a region-less tag's language and takes a region only from a device preference
 * in that same language, so bare `pl` resolves to `pl` and gets Polish conventions without a region
 * being added for it. Measured on 0.1.20:
 *
 *     pl  ->  presentation=pl  week=Monday  "17:05"  "5.03.2026"
 *
 * So `pl` needs no special handling in this template.
 */
export const SUPPORTED_LOCALES = [
  'en',
  'en-AU',
  'en-CA',
  'en-GB',
  'en-IE',
  'en-US',
  'de-DE',
  'es-ES',
  'fr-CA',
  'fr-FR',
  'hu-HU',
  'it-IT',
  'nb-NO',
  'nl-NL',
  'pl',
  'pt-PT',
  'sk-SK',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
